import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SquarePen, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  FieldGroup,
  FieldSet,
  FieldLegend,
  FieldSeparator,
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
} from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Clubhouse_LeagueScoring } from "../../enums";
import { PinType, SkyConditions, WindDirection, WindSpeed } from "../../enums";
import { extractServerErrorLines, formatUtcWindow } from "../helpers";
import {
  roundItemFieldsSchema,
  seasonRoundItemSchema,
  type SeasonRoundItem,
} from "../leagueFormSchema";
import { WEEKLY_WINDOW_REQUIRED } from "./roundPlan";
import { RoundSettingSelect } from "./RoundSettingSelect";
import {
  PIN_OPTIONS,
  WIND_SPEED_OPTIONS,
  WIND_DIRECTION_OPTIONS,
  SKY_OPTIONS,
  GROUND_OPTIONS,
  TIME_OF_DAY_OPTIONS,
  AUTO_CONCEDE_OPTIONS,
  AUTO_CONTINUE_OPTIONS,
  AUTO_GIMME_OPTIONS,
  GIMME_DISTANCE_OPTIONS,
  STIMP_OPTIONS,
} from "./roundSettingsOptions";
import { useFacilityCourses } from "modules/course/courseHooks";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import { ALL_18_HOLES, FRONT_9_HOLES } from "./roundMapping";

type HolesPreset = "front9" | "back9" | "all18";

// "All 18" must carry the explicit full hole list, not undefined: on PATCH an
// undefined selectedHoles is dropped from the request body, so switching from
// Front/Back 9 to All 18 would silently keep the old 9-hole selection. Sending
// [1..18] overwrites it (and still round-trips to the all18 preset below).
const HOLES_PRESET_SELECTED: Record<HolesPreset, number[]> = {
  front9: FRONT_9_HOLES,
  back9: [10, 11, 12, 13, 14, 15, 16, 17, 18],
  all18: ALL_18_HOLES,
};

function presetFromSelected(selected?: number[]): HolesPreset {
  if (!selected || selected.length === 0) return "front9";
  if (selected.length === 18) return "all18";
  if (selected[0] === 1) return "front9";
  return "back9";
}

// Same size as the dialog title (text-base), heavier than the field labels
// (text-sm). Must not outrank "Edit Round".
const readOnlyValueClass = "text-base font-bold leading-tight text-text-primary";
const readOnlyValueStyle = {
  fontFamily: "Poppins, ui-sans-serif, system-ui",
  fontWeight: 700,
} as const;

/**
 * @param dueByUtc Seeded for a Season Flex round, which has no play window to
 *   carry its schedule. The create contract wants exactly one of the two, so a
 *   flex round with neither would POST no scheduling field at all — the season
 *   end is what the round planner already uses for generated flex rounds.
 */
function makeDefaultValues(
  roundNumber: number,
  dueByUtc?: string,
  assignedPlayWindow?: { start: string; end: string },
): SeasonRoundItem {
  return {
    roundNumber,
    courseId: 0,
    // The operator never sets this. A weekly Add Round is handed the window
    // the schedule already assigned. Blank is not a weekly window — the
    // dialog refuses to save rather than letting the create mapper substitute
    // the season bounds.
    playWindowStartUtc: assignedPlayWindow?.start ?? "",
    playWindowEndUtc: assignedPlayWindow?.end ?? "",
    ...(dueByUtc ? { dueByUtc } : {}),
    name: "",
    settings: {
      selectedHoles: FRONT_9_HOLES,
      pinType: PinType.NOVICE,
      windSpeed: WindSpeed.CALM,
      windDirection: WindDirection.NORTH,
      skyConditions: SkyConditions.SUNNY,
      stimpSetting: 8.5,
    },
  };
}

interface RoundEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  onSave: (round: SeasonRoundItem) => void | Promise<void>;
  initialValues?: SeasonRoundItem;
  nextRoundNumber?: number;
  /** League's home facility; falls back to the current facility when omitted. */
  facilityId?: string;
  /** The league's scoring type; a round's competition type is locked to it. */
  leagueScoringType?: Clubhouse_LeagueScoring;
  /** League season end (ISO yyyy-MM-dd). A Flex create uses it as dueByUtc. */
  seasonEndDate?: string;
  /**
   * Window the weekly schedule already assigned to a new round. Seeded into
   * the create payload and not shown — play windows are not editable here.
   */
  assignedPlayWindow?: { start: string; end: string };
  /** Season Flex league: a create seeds dueByUtc from the season end. */
  isFlex?: boolean;
}

export function RoundEditorDialog({
  open,
  onOpenChange,
  mode,
  onSave,
  initialValues,
  nextRoundNumber = 1,
  facilityId: facilityIdProp,
  seasonEndDate,
  assignedPlayWindow,
  isFlex = false,
}: RoundEditorDialogProps) {
  const isEditing = mode === "edit";
  // Only a flex round needs one, and only on create — an existing round keeps
  // whatever schedule it was saved with.
  const flexDueBy = isFlex ? seasonEndDate : undefined;
  // Weekly create must carry the schedule's window. Edit and Flex do not:
  // edit leaves the stored window alone, and Flex uses the season bounds.
  const weeklyWindowMissing =
    !isEditing &&
    !isFlex &&
    !assignedPlayWindow &&
    !initialValues?.playWindowStartUtc;
  const [formError, setFormError] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<SeasonRoundItem>({
    // Weekly create still requires a play window (the schedule-assigned one).
    // Edit and Flex do not: the operator can't change a window, and a flex
    // round has none of its own.
    resolver: zodResolver(
      !isEditing && !isFlex ? seasonRoundItemSchema : roundItemFieldsSchema,
    ) as Resolver<SeasonRoundItem>,
    defaultValues:
      initialValues ??
      makeDefaultValues(nextRoundNumber, flexDueBy, assignedPlayWindow),
  });

  const { control, handleSubmit, reset, setValue } = form;

  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;
  const [courses, coursesQueryState] = useFacilityCourses(facilityId ?? "");
  const coursesLoading = !coursesQueryState?.isFinished;

  // Reset only on the open transition (false → true), seeding from the current
  // round. Resetting on every initialValues/nextRoundNumber change would let a
  // background refetch (both are derived from RTK query data) wipe the
  // operator's in-progress edits while the dialog is open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset(
        initialValues ??
          makeDefaultValues(nextRoundNumber, flexDueBy, assignedPlayWindow),
      );
      setFormError(weeklyWindowMissing ? [WEEKLY_WINDOW_REQUIRED] : null);
    }
    wasOpen.current = open;
  }, [
    open,
    initialValues,
    nextRoundNumber,
    flexDueBy,
    assignedPlayWindow,
    weeklyWindowMissing,
    reset,
  ]);

  const nameValue = useWatch({ control, name: "name" });
  const startValue = useWatch({ control, name: "playWindowStartUtc" });
  const endValue = useWatch({ control, name: "playWindowEndUtc" });
  const courseId = useWatch({ control, name: "courseId" });
  const playWindowLabel =
    startValue && endValue ? formatUtcWindow(startValue, endValue) : undefined;
  const selectedHoles = useWatch({ control, name: "settings.selectedHoles" });

  const holesPreset = presetFromSelected(selectedHoles);
  const courseOptions = useMemo(
    () =>
      courses.map((c) => ({
        value: c.id,
        label: c.displayName,
        key: c.shortName,
      })),
    [courses],
  );

  async function onValidSubmit(values: SeasonRoundItem) {
    setFormError(null);
    setIsSaving(true);
    try {
      await onSave(values);
      onOpenChange(false);
    } catch (err) {
      const lines = extractServerErrorLines(err);
      setFormError(
        lines.length > 0
          ? lines
          : ["Couldn't save this round. Please try again."],
      );
    } finally {
      setIsSaving(false);
    }
  }

  const formId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl"
        header={
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="poppins flex items-center gap-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                <SquarePen className="size-4" aria-hidden="true" />
              </span>
              {isEditing ? "Edit Round" : "Add Round"}
            </DialogTitle>
            <DialogDescription className="mulish">
              Configure the round’s course and playing conditions.
            </DialogDescription>
          </DialogHeader>
        }
        footer={
          <div className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              disabled={isSaving}
              className="rounded-xl"
            >
              {isSaving && <Spinner data-icon="inline-start" />}
              {isSaving ? "Saving…" : isEditing ? "Save changes" : "Add round"}
            </Button>
          </div>
        }
      >
        <FormProvider {...form}>
          <form id={formId} onSubmit={handleSubmit(onValidSubmit)}>
            <div className="py-5">
              {formError && (
                <Alert variant="destructive" className="mb-5">
                  <TriangleAlert />
                  <AlertTitle>Unable to save this round</AlertTitle>
                  <AlertDescription>
                    <ul className="mt-1 flex flex-col gap-1 list-disc pl-4">
                      {formError.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <FieldGroup>
                {/* ── Basics ─────────────────────────────────────────────── */}
                {/* Name and play window are assigned by the schedule. Shown
                    read-only — never as inputs — on Weekly and Flex alike. */}
                {isEditing && (
                  <FieldSet>
                    <FieldLegend className="poppins text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Round
                    </FieldLegend>
                    <Field>
                      <FieldLabel>Round name</FieldLabel>
                      <p className={readOnlyValueClass} style={readOnlyValueStyle}>
                        {nameValue}
                      </p>
                      <FieldDescription>
                        Round names are assigned automatically.
                      </FieldDescription>
                    </Field>
                    {playWindowLabel && (
                      <Field>
                        <FieldLabel>Play window</FieldLabel>
                        <p className={readOnlyValueClass} style={readOnlyValueStyle}>
                          {playWindowLabel}
                        </p>
                        <FieldDescription>
                          Set by the schedule. Not editable.
                        </FieldDescription>
                      </Field>
                    )}
                  </FieldSet>
                )}
                {isEditing && <FieldSeparator />}

                {/* ── Course & Holes ─────────────────────────────────────── */}
                <FieldSet>
                  <FieldLegend className="poppins text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Course &amp; Holes
                  </FieldLegend>
                  <Field>
                    <FieldLabel htmlFor="round-course">Course</FieldLabel>
                    <Controller
                      name="courseId"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <select
                            id="round-course"
                            // Native <option> popups don't inherit theme colors —
                            // in dark mode the browser draws them on a light
                            // default bg with light text. Color the control and
                            // its options from theme tokens, and opt the native
                            // popup into the dark UA scheme so it stays legible.
                            className="border-input h-9 w-full rounded-lg border bg-transparent px-3 text-sm text-foreground shadow-xs dark:[color-scheme:dark] [&>option]:bg-popover [&>option]:text-popover-foreground"
                            value={field.value || ""}
                            disabled={coursesLoading}
                            onChange={(e) => {
                              field.onChange(Number(e.target.value));
                              // Reset holes to the Front 9 default for the new
                              // course. Use the explicit list (not undefined)
                              // so the reset actually persists on save.
                              setValue("settings.selectedHoles", FRONT_9_HOLES);
                            }}
                            aria-invalid={!!fieldState.error}
                          >
                            <option value="">
                              {coursesLoading
                                ? "Loading courses…"
                                : "Select a course"}
                            </option>
                            {courseOptions.map((c) => (
                              <option key={c.key} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                          <FieldError
                            errors={
                              fieldState.error ? [fieldState.error] : undefined
                            }
                          />
                        </>
                      )}
                    />
                  </Field>

                  {courseId > 0 && (
                    <Field>
                      <FieldLabel>Holes</FieldLabel>
                      <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
                        {(
                          [
                            { value: "front9", label: "Front 9" },
                            { value: "back9", label: "Back 9" },
                            { value: "all18", label: "All 18" },
                          ] as const
                        ).map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() =>
                              setValue(
                                "settings.selectedHoles",
                                HOLES_PRESET_SELECTED[value],
                                { shouldDirty: true },
                              )
                            }
                            className={cn(
                              "h-8 rounded-lg text-sm font-semibold transition-colors",
                              holesPreset === value
                                ? "bg-card text-text-primary shadow-sm"
                                : "text-text-muted hover:text-text-primary",
                            )}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </Field>
                  )}
                </FieldSet>

                <FieldSeparator />

                {/* ── Conditions ─────────────────────────────────────────── */}
                <FieldSet>
                  <FieldLegend className="poppins text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Conditions
                  </FieldLegend>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <RoundSettingSelect
                      name="settings.pinType"
                      label="Pin position"
                      options={PIN_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.stimpSetting"
                      label="Green speed (stimp)"
                      options={STIMP_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.windSpeed"
                      label="Wind speed"
                      options={WIND_SPEED_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.windDirection"
                      label="Wind direction"
                      options={WIND_DIRECTION_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.skyConditions"
                      label="Sky"
                      options={SKY_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.groundConditions"
                      label="Ground"
                      options={GROUND_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.timeConditions"
                      label="Time of day"
                      options={TIME_OF_DAY_OPTIONS}
                    />
                  </div>
                </FieldSet>

                <FieldSeparator />

                {/* ── Automation ─────────────────────────────────────────── */}
                <FieldSet>
                  <FieldLegend className="poppins text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Automation
                  </FieldLegend>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <RoundSettingSelect
                      name="settings.autoConcede"
                      label="Auto concede"
                      options={AUTO_CONCEDE_OPTIONS}
                      description="Strokes before a hole is auto-conceded."
                    />
                    <RoundSettingSelect
                      name="settings.autoContinue"
                      label="Auto continue"
                      options={AUTO_CONTINUE_OPTIONS}
                      description="Wait before advancing to the next shot."
                    />
                    <RoundSettingSelect
                      name="settings.gimmeDistance"
                      label="Gimme distance"
                      options={GIMME_DISTANCE_OPTIONS}
                    />
                    <RoundSettingSelect
                      name="settings.autoGimmes"
                      label="Auto gimme"
                      options={AUTO_GIMME_OPTIONS}
                      description="Strokes a gimme costs a player."
                    />
                  </div>
                  <Field orientation="horizontal">
                    <FieldLabel htmlFor="settings.autoDrop">
                      Auto drop
                    </FieldLabel>
                    <Controller
                      name="settings.autoDrop"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="settings.autoDrop"
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </Field>
                  <Field orientation="horizontal">
                    <FieldLabel htmlFor="settings.mulligans">
                      Allow mulligans
                    </FieldLabel>
                    <Controller
                      name="settings.mulligans"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="settings.mulligans"
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </Field>
                </FieldSet>
              </FieldGroup>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
